import {
  ApiOperationType,
  BrandDetailResponse,
  CategoryResponse,
  DepartmentResponse,
  EntityListResponse,
  ProductLookupRequest,
  ProductLookupResponse,
  RegionResponse,
  ScopeCreateRequest,
  ScopeOverviewResponse,
  ScopeResponse,
  ScopeSectionCreateRequest,
  ScopeSectionResponse,
  ScopeSectionRuleReplaceRequest,
  ScopeSectionRuleTypeName,
  ScopeUpdateRequest,
  ScopeUsageResponse,
  SkuClassResponse,
  WarehouseResponse,
  WarehouseTypeResponse,
} from '@ydistri/api-sdk';
import { InfiniteScrollParams, serializeFullApiBasedQueryArgs } from '@ydistri/ds';
import { apiSlice, ErrorType } from './api';
import { getTags } from './apiLib';
import { signalrClient } from '../signalr/client';
import { SignalScopeChanged } from '../signalr/signalrInterfaces';
import {
  BrandsCollection,
  CategoriesCollection,
  DepartmentsCollection,
  EntityListsCollection,
  ProductsCollection,
  RegionsCollection,
  SkuClassesCollection,
  ScopesCollection,
  StoresCollection,
  WarehouseTypesCollection,
} from '../swagger/collections';
import {
  applyScopeChangeToList,
  isScopeSignalForProject,
  isScopeSnapshotNewer,
  toScopeOverview,
} from './scopeSignalrCacheLib';

const { TAGS, TAGS_ARRAY } = getTags('scopesAdministration');

export interface GetScopesRequest extends InfiniteScrollParams {
  projectShortName: string;
}

export interface GetScopeRequest {
  projectShortName: string;
  scopeId: number;
}

export interface UpdateScopePayload {
  scopeId: number;
  request: ScopeUpdateRequest;
}

export interface CreateScopeSectionPayload {
  scopeId: number;
  request: ScopeSectionCreateRequest;
}

export interface PutScopeSectionRulePayload {
  scopeId: number;
  scopeSectionId: number;
  ruleType: ScopeSectionRuleTypeName;
  request: ScopeSectionRuleReplaceRequest;
}

export interface DeleteScopeSectionPayload {
  scopeId: number;
  scopeSectionId: number;
}

export interface DeleteScopeSectionRulePayload extends DeleteScopeSectionPayload {
  scopeSectionRuleTypeId: ScopeSectionRuleTypeName;
}

export interface ScopeSectionRuleOptions {
  brands: BrandDetailResponse[];
  categories: CategoryResponse[];
  departments: DepartmentResponse[];
  entityLists: EntityListResponse[];
  regions: RegionResponse[];
  skuClasses: SkuClassResponse[];
  stores: WarehouseResponse[];
  storeTypes: WarehouseTypeResponse[];
}

export const apiScopes = apiSlice.enhanceEndpoints({ addTagTypes: TAGS_ARRAY }).injectEndpoints({
  endpoints: builder => ({
    getScopes: builder.query<ScopeOverviewResponse[], GetScopesRequest>({
      queryFn: async ({ projectShortName: _projectShortName, ...query }) => {
        try {
          const response = await ScopesCollection.scopesList(query);
          return { data: response.data.data };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
          const err = error as ErrorType;
          return { error: err };
        }
      },
      providesTags: result => [
        TAGS.scopesList,
        ...(result ?? []).map(selection => ({
          type: TAGS.scope,
          id: selection.scopeId,
        })),
      ],
      serializeQueryArgs: serializeFullApiBasedQueryArgs(),
      async onCacheEntryAdded(arg, api) {
        const latestScopeSnapshots = new Map<number, ScopeOverviewResponse>();
        const listener = (signal: SignalScopeChanged) => {
          if (!isScopeSignalForProject(signal.projectSlug, arg.projectShortName)) return;

          if (
            signal.operationType !== ApiOperationType.Create &&
            signal.operationType !== ApiOperationType.Patch &&
            signal.operationType !== ApiOperationType.Replace
          ) {
            return;
          }

          const selection = toScopeOverview(signal.data);
          const latestScopeSnapshot = latestScopeSnapshots.get(selection.scopeId);
          if (latestScopeSnapshot && !isScopeSnapshotNewer(latestScopeSnapshot, selection)) return;

          api.updateCachedData(draft => {
            const nextScopes = applyScopeChangeToList(draft, selection, arg, latestScopeSnapshot);
            draft.splice(0, draft.length, ...nextScopes);
          });
          latestScopeSnapshots.set(selection.scopeId, selection);
          api.dispatch(
            apiScopes.util.invalidateTags([{ type: TAGS.scopeSkuCount, id: selection.scopeId }]),
          );
        };

        try {
          await api.cacheDataLoaded;
          for (const scope of api.getCacheEntry().data ?? []) {
            latestScopeSnapshots.set(scope.scopeId, scope);
          }
          signalrClient.on('ScopeChanged', listener);
          await api.cacheEntryRemoved;
        } catch {
          // The entry can be removed before its initial request resolves.
        } finally {
          signalrClient.off('ScopeChanged', listener);
        }
      },
    }),

    getScope: builder.query<ScopeResponse, GetScopeRequest>({
      queryFn: async ({ scopeId }) => {
        try {
          const response = await ScopesCollection.getScope(scopeId);
          return { data: response.data.data };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
          const err = error as ErrorType;
          return { error: err };
        }
      },
      providesTags: (result, error, { scopeId }) => [{ type: TAGS.scope, id: scopeId }],
      async onCacheEntryAdded({ projectShortName, scopeId }, api) {
        const listener = (signal: SignalScopeChanged) => {
          if (
            signal.entityId !== scopeId ||
            !isScopeSignalForProject(signal.projectSlug, projectShortName) ||
            (signal.operationType !== ApiOperationType.Create &&
              signal.operationType !== ApiOperationType.Patch &&
              signal.operationType !== ApiOperationType.Replace)
          ) {
            return;
          }

          const currentScope = api.getCacheEntry().data;
          if (!currentScope || !isScopeSnapshotNewer(currentScope, signal.data)) return;

          api.updateCachedData(() => signal.data);

          api.dispatch(
            apiScopes.util.invalidateTags([{ type: TAGS.scopeSkuCount, id: signal.data.scopeId }]),
          );
        };

        try {
          await api.cacheDataLoaded;
          signalrClient.on('ScopeChanged', listener);
          await api.cacheEntryRemoved;
        } catch {
          // The entry can be removed before its initial request resolves.
        } finally {
          signalrClient.off('ScopeChanged', listener);
        }
      },
    }),

    getScopeUsages: builder.query<ScopeUsageResponse[], GetScopeRequest>({
      queryFn: async ({ scopeId }) => {
        try {
          const response = await ScopesCollection.getScopeUsages(scopeId);
          return { data: response.data.data };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
          const err = error as ErrorType;
          return { error: err };
        }
      },
      providesTags: (result, error, { scopeId }) => [{ type: TAGS.scopeUsages, id: scopeId }],
    }),

    createScope: builder.mutation<ScopeResponse, ScopeCreateRequest>({
      queryFn: async request => {
        try {
          const response = await ScopesCollection.createScope(request);
          return { data: response.data.data };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
          const err = error as ErrorType;
          return { error: err };
        }
      },
      invalidatesTags: [TAGS.scopesList],
    }),

    updateScope: builder.mutation<null, UpdateScopePayload>({
      queryFn: async ({ scopeId, request }) => {
        try {
          await ScopesCollection.updateScope(scopeId, request);
          return { data: null };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
          const err = error as ErrorType;
          return { error: err };
        }
      },
    }),

    createScopeSection: builder.mutation<ScopeSectionResponse, CreateScopeSectionPayload>({
      queryFn: async ({ scopeId, request }) => {
        try {
          const response = await ScopesCollection.createScopeSection(scopeId, request);
          return { data: response.data.data };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
          const err = error as ErrorType;
          return { error: err };
        }
      },
      invalidatesTags: (result, error, { scopeId }) => [{ type: TAGS.scopeSkuCount, id: scopeId }],
    }),

    putScopeSectionRule: builder.mutation<null, PutScopeSectionRulePayload>({
      queryFn: async ({ scopeId, scopeSectionId, ruleType, request }) => {
        try {
          await ScopesCollection.putScopeSectionRule(scopeId, scopeSectionId, ruleType, request);
          return { data: null };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
          const err = error as ErrorType;
          return { error: err };
        }
      },
      invalidatesTags: (result, error, { scopeId }) => [{ type: TAGS.scopeSkuCount, id: scopeId }],
    }),

    deleteScopeSection: builder.mutation<null, DeleteScopeSectionPayload>({
      queryFn: async ({ scopeId, scopeSectionId }) => {
        try {
          await ScopesCollection.deleteScopeSection(scopeId, scopeSectionId);
          return { data: null };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
          const err = error as ErrorType;
          return { error: err };
        }
      },
      invalidatesTags: (result, error, { scopeId }) => [{ type: TAGS.scopeSkuCount, id: scopeId }],
    }),

    deleteScopeSectionRule: builder.mutation<null, DeleteScopeSectionRulePayload>({
      queryFn: async ({ scopeId, scopeSectionId, scopeSectionRuleTypeId }) => {
        try {
          await ScopesCollection.deleteScopeSectionRule(
            scopeId,
            scopeSectionId,
            scopeSectionRuleTypeId,
          );
          return { data: null };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
          const err = error as ErrorType;
          return { error: err };
        }
      },
      invalidatesTags: (result, error, { scopeId }) => [{ type: TAGS.scopeSkuCount, id: scopeId }],
    }),

    lookupScopeProducts: builder.mutation<ProductLookupResponse, ProductLookupRequest>({
      queryFn: async request => {
        try {
          const response = await ProductsCollection.lookupProducts(request);
          return { data: response.data.data };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
          const err = error as ErrorType;
          return { error: err };
        }
      },
    }),

    getScopeSectionRuleOptions: builder.query<ScopeSectionRuleOptions, void>({
      queryFn: async () => {
        try {
          const [
            categories,
            regions,
            storeTypes,
            stores,
            departments,
            skuClasses,
            brands,
            entityLists,
          ] = await Promise.all([
            CategoriesCollection.categoriesList(),
            RegionsCollection.regionsList(),
            WarehouseTypesCollection.warehouseTypesList(),
            StoresCollection.warehousesList(),
            DepartmentsCollection.departmentsList(),
            SkuClassesCollection.skuClassesList(),
            BrandsCollection.brandsList(),
            EntityListsCollection.entityListsList(),
          ]);

          return {
            data: {
              categories: categories.data.data,
              regions: regions.data.data,
              storeTypes: storeTypes.data.data,
              stores: stores.data.data,
              departments: departments.data.data,
              skuClasses: skuClasses.data.data,
              brands: brands.data.data,
              entityLists: entityLists.data.data,
            },
          };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
          const err = error as ErrorType;
          return { error: err };
        }
      },
    }),

    getScopeSkuCount: builder.query<number, number>({
      queryFn: async scopeId => {
        try {
          const response = await ScopesCollection.getScopeSkuCount(scopeId);
          return { data: response.data.data.count };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
          const err = error as ErrorType;
          return { error: err };
        }
      },
      providesTags: (result, error, scopeId) => [{ type: TAGS.scopeSkuCount, id: scopeId }],
    }),
  }),
});

export const {
  useGetScopesQuery,
  useGetScopeQuery,
  useGetScopeUsagesQuery,
  useCreateScopeMutation,
  useUpdateScopeMutation,
  useCreateScopeSectionMutation,
  usePutScopeSectionRuleMutation,
  useDeleteScopeSectionMutation,
  useDeleteScopeSectionRuleMutation,
  useLookupScopeProductsMutation,
  useGetScopeSectionRuleOptionsQuery,
  useGetScopeSkuCountQuery,
} = apiScopes;
